import { z } from 'zod';
import { IsoDateTime, Uuid, patchOf } from './common';
import { PricingCalculateResponse } from './pricing';

export const ProductStatus = z
  .enum(['idea', 'prototype', 'active', 'archived'])
  .describe('Ideia → Protótipo → Ativo → Arquivado.');
export type ProductStatus = z.infer<typeof ProductStatus>;
export const PRODUCT_STATUS_LABELS: Record<ProductStatus, string> = {
  idea: 'Ideia',
  prototype: 'Protótipo',
  active: 'Ativo',
  archived: 'Arquivado',
};
export const PRODUCT_STATUS_HELP: Record<ProductStatus, string> = {
  idea: 'Só um rascunho: não aparece no Orçamento rápido.',
  prototype: 'Em teste: aparece no Orçamento rápido, mas não nos relatórios de catálogo.',
  active: 'À venda: aparece no catálogo, no Orçamento rápido e nos relatórios.',
  archived: 'Fora do catálogo. Pedidos e arquivos continuam guardados.',
};

export const ProductOrigin = z.enum(['own', 'third_party']);
export type ProductOrigin = z.infer<typeof ProductOrigin>;
export const ProductLicense = z.enum(['unknown', 'personal', 'cc_by_nc', 'cc_by', 'commercial']);
export type ProductLicense = z.infer<typeof ProductLicense>;
export const LICENSE_LABELS: Record<ProductLicense, string> = {
  unknown: 'Não sei / não informada',
  personal: 'Uso pessoal apenas',
  cc_by_nc: 'CC BY-NC (não comercial)',
  cc_by: 'CC BY (permite venda)',
  commercial: 'Licença comercial comprada',
};

/** A third-party design may only be sold with a license that allows it. */
export function canSell(p: { origin: ProductOrigin; license: ProductLicense | null }): boolean {
  return p.origin === 'own' || p.license === 'cc_by' || p.license === 'commercial';
}

/** Suggested categories (free text; the ones in use are added by the API). */
export const DEFAULT_CATEGORIES = [
  'Casa e decoração',
  'Acessórios',
  'Organização',
  'Iluminação',
  'Troféus',
  'Miniaturas',
];

const strip = (s: string) => s.normalize('NFD').replace(/[̀-ͯ]/g, '').toUpperCase();

/** Connectors skipped in SKUs ("Suporte de Celular" → "SUP-CEL"). */
const CONNECTORS = new Set([
  'DE',
  'DA',
  'DO',
  'DAS',
  'DOS',
  'E',
  'COM',
  'PARA',
  'P',
  'EM',
  'A',
  'O',
]);

/** "Vaso Ondas" → "VAS-OND"; "Suporte de Celular" → "SUP-CEL"; "Chaveiro" → "CHA"; "" → "". */
export function skuOf(name: string): string {
  const all = strip(name)
    .replace(/[^A-Z0-9]+/g, ' ')
    .trim()
    .split(/\s+/)
    .filter(Boolean);
  const words = all.filter((w, i) => i === 0 || !CONNECTORS.has(w));
  return words
    .slice(0, 2)
    .map((w) => w.slice(0, 3))
    .join('-');
}

const skuPart = (s: string | null, n: number) =>
  s
    ? strip(s)
        .replace(/[^A-Z0-9]+/g, '')
        .slice(0, n)
    : '';

/** Variant SKU: product SKU + size (+ first 3 letters of the color when given). */
export function variantSkuOf(
  productSku: string,
  size: string | null,
  color: string | null,
): string {
  return [productSku, skuPart(size, 4), skuPart(color, 3)].filter(Boolean).join('-');
}

const Text = (max: number) =>
  z
    .string()
    .trim()
    .max(max)
    .nullable()
    .transform((v) => (v === '' ? null : v));

const ProductFields = z.object({
  name: z.string().trim().min(1, 'Dê um nome ao produto.').max(120),
  sku: z
    .string()
    .trim()
    .toUpperCase()
    .regex(/^[A-Z0-9][A-Z0-9-]{0,39}$/, 'Use letras, números e hífen.')
    .optional()
    .describe('Internal SKU; generated from the name when left out ("VAS-OND").'),
  status: ProductStatus.exclude(['archived']).default('idea'),
  category: Text(80).default(null),
  tags: z
    .array(z.string().trim().toLowerCase().min(1).max(40))
    .max(30)
    .default([])
    .transform((t) => [...new Set(t)]),
  description: Text(4000).default(null),
  internalNotes: Text(4000).default(null),
  origin: ProductOrigin.default('own'),
  sourceUrl: Text(500)
    .default(null)
    .describe('Link to the third-party model (MakerWorld, Printables…).'),
  license: ProductLicense.nullable().default(null),
  custom: z.boolean().default(false).describe('"Sob encomenda": a one-off design for a customer.'),
  customerId: Uuid.nullable().default(null),
  externalRef: Text(200).default(null),
});
export const UpdateProductBody = patchOf(
  ProductFields.extend({ status: ProductStatus.exclude(['archived']) }),
);
export type UpdateProductBody = z.input<typeof UpdateProductBody>;

/** Print profile of a variant: the Quick Quote plates, labor and materials. */
export const VariantProfile = z
  .object({
    printerIds: z
      .array(Uuid)
      .max(10)
      .default([])
      .describe('Compatible printers; the first is used for cost.'),
    plates: z
      .array(
        z.object({
          printSeconds: z
            .number()
            .int()
            .min(0)
            .max(30 * 24 * 3600),
          unitsPerPlate: z.number().int().min(1).max(1000),
          slots: z
            .array(
              z.object({
                filamentId: Uuid.nullable(),
                grams: z.number().min(0).max(100_000),
                nozzle: z.enum(['main', 'aux']).nullable().default(null),
              }),
            )
            .max(32),
        }),
      )
      .max(50)
      .default([]),
    batchPlates: z.number().int().min(1).max(1000).default(1).describe('Plates per batch (lote).'),
    labor: z
      .object({
        finishingSecondsPerUnit: z.number().int().min(0).max(86_400).default(180),
        packingSecondsPerUnit: z.number().int().min(0).max(86_400).default(300),
      })
      .default({ finishingSecondsPerUnit: 180, packingSecondsPerUnit: 300 }),
    packagingKitId: Uuid.nullable().default(null),
    supplies: z
      .array(z.object({ supplyId: Uuid, quantityPerUnit: z.number().min(0).max(10_000) }))
      .max(50)
      .default([]),
  })
  .meta({ id: 'VariantProfile' });
export type VariantProfile = z.infer<typeof VariantProfile>;
export type VariantProfileInput = z.input<typeof VariantProfile>;

const VariantFields = z.object({
  name: z.string().trim().min(1).max(80).describe('E.g. "M · Coral".'),
  sku: z
    .string()
    .trim()
    .toUpperCase()
    .regex(/^[A-Z0-9][A-Z0-9-]{0,49}$/, 'Use letras, números e hífen.')
    .optional()
    .describe('Generated from the product SKU, size and color when left out.'),
  size: Text(40).default(null),
  color: Text(40).default(null),
  profile: VariantProfile.default(VariantProfile.parse({})),
  slicedVersionId: Uuid.nullable()
    .default(null)
    .describe('The sliced product file version the profile came from.'),
  sort: z.number().int().min(0).max(10_000).optional(),
  externalRef: Text(200).default(null),
});
export const CreateVariantBody = VariantFields.meta({ id: 'CreateVariantBody' });
export type CreateVariantBody = z.input<typeof CreateVariantBody>;

export const CreateProductBody = ProductFields.extend({
  variant: VariantFields.extend({
    slicedFileId: Uuid.nullable()
      .default(null)
      .describe(
        'A sliced file (e.g. the one read by Quick Quote) saved as the variant’s first file version; ' +
          'copied when it already belongs to a quote.',
      ),
  })
    .optional()
    .describe('"Salvar como produto": also creates the first variant with its print profile.'),
}).meta({
  id: 'CreateProductBody',
  example: { name: 'Vaso Ondas', category: 'Casa e decoração', status: 'prototype' },
});
export type CreateProductBody = z.input<typeof CreateProductBody>;
export const UpdateVariantBody = patchOf(VariantFields);
export type UpdateVariantBody = z.input<typeof UpdateVariantBody>;

export const VariantCost = z.object({
  unitCostCentavos: z.number().int(),
  perUnit: z.object({ grams: z.number(), printSeconds: z.number().int() }),
  batch: z.object({
    plates: z.number().int(),
    units: z.number().int(),
    printSeconds: z.number().int(),
  }),
  breakdown: PricingCalculateResponse.shape.cost.describe('Engine cost rows (centavos).'),
});
export type VariantCost = z.infer<typeof VariantCost>;

export const Variant = z
  .object({
    id: Uuid,
    productId: Uuid,
    name: z.string(),
    sku: z.string(),
    size: z.string().nullable(),
    color: z.string().nullable(),
    colorHex: z.string().nullable().describe('From the first slot filament.'),
    material: z
      .string()
      .nullable()
      .describe('From the profile filaments, e.g. "PLA" or "PLA + PETG".'),
    sort: z.number().int(),
    profile: VariantProfile,
    slicedVersionId: Uuid.nullable(),
    cost: VariantCost.nullable(),
    costMissing: z.string().nullable().describe('Why there is no cost yet (pt-BR).'),
    stock: z
      .object({ onHand: z.number().int(), reserved: z.number().int(), available: z.number().int() })
      .describe('Pronta entrega.'),
    externalRef: z.string().nullable(),
    createdAt: IsoDateTime,
    updatedAt: IsoDateTime,
  })
  .meta({ id: 'Variant' });
export type Variant = z.infer<typeof Variant>;

const ProductBase = z.object({
  id: Uuid,
  name: z.string(),
  sku: z.string(),
  status: ProductStatus,
  category: z.string().nullable(),
  tags: z.array(z.string()),
  origin: ProductOrigin,
  license: ProductLicense.nullable(),
  canSell: z.boolean(),
  custom: z.boolean(),
  customer: z.object({ id: Uuid, name: z.string() }).nullable(),
  materials: z.array(z.string()),
  variantCount: z.number().int(),
  stockUnits: z.number().int().describe('Pronta entrega: units on hand across variants.'),
  coverUrl: z.string().nullable(),
  archivedAt: IsoDateTime.nullable(),
  createdAt: IsoDateTime,
  updatedAt: IsoDateTime,
});

export const ProductSummary = ProductBase.extend({
  fromPriceCentavos: z.number().int().nullable().describe('Lowest listed price among variants.'),
  marginAlert: z
    .object({ level: z.enum(['warn', 'err']), text: z.string() })
    .nullable()
    .describe('Worst variant × channel margin, or the license block.'),
}).meta({ id: 'ProductSummary' });
export type ProductSummary = z.infer<typeof ProductSummary>;

export const Product = ProductBase.extend({
  description: z.string().nullable(),
  internalNotes: z.string().nullable(),
  sourceUrl: z.string().nullable(),
  externalRef: z.string().nullable(),
  variants: z.array(Variant),
  counts: z.object({
    media: z.number().int(),
    files: z.number().int(),
    listings: z.number().int(),
  }),
}).meta({ id: 'Product' });
export type Product = z.infer<typeof Product>;

export const ProductList = z.object({
  data: z.array(ProductSummary),
  nextCursor: z.string().nullable(),
  counts: z
    .object({
      all: z.number().int(),
      idea: z.number().int(),
      prototype: z.number().int(),
      active: z.number().int(),
      archived: z.number().int(),
      custom: z.number().int(),
    })
    .describe('Per segment for the same filters; "all" excludes archived.'),
});
export const ProductQuery = z.object({
  segment: z.enum(['all', 'idea', 'prototype', 'active', 'archived', 'custom']).default('all'),
  category: z.string().trim().max(80).optional(),
  material: z.string().trim().max(40).optional(),
  tag: z.string().trim().max(40).optional(),
  q: z.string().trim().max(100).optional(),
  sort: z.enum(['recent', 'name']).default('recent'),
});
export const CategoryList = z.object({ data: z.array(z.string()) });

/** "Começar de um produto" and the quote "Adicionar item" list. */
export const CatalogVariant = z.object({
  product: z.object({ id: Uuid, name: z.string(), sku: z.string(), status: ProductStatus }),
  variant: z.object({ id: Uuid, name: z.string(), sku: z.string() }),
  coverUrl: z.string().nullable(),
  unitCostCentavos: z.number().int().nullable(),
  suggestedPriceCentavos: z
    .number()
    .int()
    .nullable()
    .describe('Rounded suggestion on `channelId` with today’s costs and fees.'),
  listedPriceCentavos: z.number().int().nullable().describe('"Preço anunciado" on `channelId`.'),
  missing: z.string().nullable().describe('Why there is no cost yet (pt-BR).'),
});
export type CatalogVariant = z.infer<typeof CatalogVariant>;
export const CatalogVariantList = z.object({ data: z.array(CatalogVariant) });
export const CatalogQuery = z.object({
  channelId: Uuid.optional().describe('Channel for the suggested and listed prices.'),
  q: z.string().trim().max(100).optional().describe('Name or SKU of the product or variant.'),
});
